import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import EmployeeDropdown from '../components/EmployeeDropdown'
import SignaturePad from '../components/SignaturePad'
import Footer from '../components/Footer'
import TermsAndConditions from '../components/TermsAndConditions'
import GoogleMapsFeedback from '../components/GoogleMapsFeedback'
import { registerVisitor } from '../services/visitorApi'
import { useToast } from '../components/ToastProvider'
import { requestNotificationPermission, registerInstallation, onMessageReceived } from '../services/fcmService'

const PURPOSE_OPTIONS = [
  { value: '', label: 'Select purpose' },
  { value: 'Interview', label: 'Interview' },
  { value: 'Meeting', label: 'Meeting' },
  { value: 'Sales', label: 'Sales' },
  { value: 'General Visit', label: 'General Visit' },
  { value: 'Other', label: 'Other' },
]

const TIME_SLOTS = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM'
]

export default function CombinedPage() {
  const navigate = useNavigate()
  const toast = useToast()
  
  const [formData, setFormData] = useState({
    fullName: '',
    organization: '',
    gender: '',
    phone: '',
    email: '',
    employeeId: '',
    purpose: '',
    detailedPurpose: '',
    reason: '',
    date: '',
    timeSlot: '',
    notes: '',
    signature: '',
    agreeToTerms: false,
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required'
    if (!formData.organization.trim()) newErrors.organization = 'Organization is required'
    if (!formData.gender) newErrors.gender = 'Gender is required'
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!/^[0-9+\-\s()]{10,}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Invalid phone number'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Invalid email address'
    }
    if (!formData.employeeId) newErrors.employeeId = 'Please select an employee'
    if (!formData.purpose) newErrors.purpose = 'Purpose is required'
    if (formData.purpose === 'Other' && !formData.detailedPurpose?.trim()) {
      newErrors.detailedPurpose = 'Please provide detailed purpose for your visit'
    }
    if (!formData.date) newErrors.date = 'Appointment date is required'
    if (!formData.reason.trim()) newErrors.reason = 'Reason is required'
    if (!formData.signature) newErrors.signature = 'Signature is required'
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions'
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validateForm()
    setErrors(validationErrors)
    
    if (Object.keys(validationErrors).length === 0) {
      setLoading(true)
      try {
        const result = await registerVisitor(formData)
        setSubmitted(true)
        toast.success('Registration successful! Thank you for visiting igus.')
        window.scrollTo({ top: 0, behavior: 'smooth' })
        
        // Register for notifications (optional)
        try {
          const hasPermission = await requestNotificationPermission()
          if (hasPermission && result.id) {
            await registerInstallation(result.id, 'visitor', result.id)
          }
        } catch (notifError) {
          console.log('Notification setup skipped (optional)')
        }
      } catch (err) {
        const errorMsg = err.message || 'Registration failed. Please try again.'
        setErrors({ server: errorMsg })
        toast.error(errorMsg)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } finally {
        setLoading(false)
      }
    } else {
      toast.error('Please fill in all required fields correctly')
    }
  }

  const handleReset = () => {
    setSubmitted(false)
    setFormData({
      fullName: '',
      organization: '',
      gender: '',
      phone: '',
      email: '',
      employeeId: '',
      purpose: '',
      reason: '',
      date: '',
      timeSlot: '',
      notes: '',
      signature: '',
      agreeToTerms: false,
    })
    setErrors({})
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const inputClass = (field) =>
    `w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200 ${
      errors[field] ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'
    }`

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
        <header className="bg-white shadow-sm">
          <div className="mx-auto w-full px-3 py-3 sm:px-6 sm:py-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2 text-center">
                <img src="/logo.png" alt="igus Logo" className="h-8 w-auto sm:h-12" />
                <div>
                  <h1 className="text-base font-bold text-gray-900 sm:text-2xl">Visitor Management System</h1>
                  <p className="text-xs text-gray-600 sm:text-sm">Monday - Friday, 9am - 5:30pm</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:justify-center sm:gap-2 sm:flex-row">
                <button
                  onClick={() => navigate('/employee-login')}
                  className="rounded-lg border border-saffron-300 px-3 py-2 text-xs font-medium text-saffron-700 transition-colors hover:bg-saffron-50 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Employee
                </button>
                <button
                  onClick={() => navigate('/admin')}
                  className="rounded-lg border border-saffron-300 px-3 py-2 text-xs font-medium text-saffron-700 transition-colors hover:bg-saffron-50 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Admin
                </button>
                <button
                  onClick={() => navigate('/security')}
                  className="rounded-lg border border-saffron-300 px-3 py-2 text-xs font-medium text-saffron-700 transition-colors hover:bg-saffron-50 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Security
                </button>
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center space-y-6">
            <div>
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mb-3 text-2xl font-bold text-gray-900">Registration Successful!</h2>
              <p className="mb-6 text-gray-600">
                Your visit has been registered successfully. Thank you for visiting igus.
              </p>
              {formData.date && formData.timeSlot && (
                <div className="rounded-lg bg-gray-50 p-4 mb-6 text-left">
                  <p className="text-sm text-gray-600"><span className="font-medium">Appointment Date:</span> {formData.date}</p>
                  <p className="text-sm text-gray-600"><span className="font-medium">Time:</span> {formData.timeSlot}</p>
                </div>
              )}
            </div>

            <GoogleMapsFeedback visitorName={formData.fullName} visitorEmail={formData.email} />

            <button
              onClick={handleReset}
              className="w-full rounded-lg bg-saffron-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-saffron-700"
            >
              Register Another Visitor
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
      <header className="bg-white shadow-sm">
          <div className="mx-auto w-full px-3 py-3 sm:px-6 sm:py-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2 text-center">
                <img src="/logo.png" alt="igus Logo" className="h-8 w-auto sm:h-12" />
                <div>
                  <h1 className="text-base font-bold text-gray-900 sm:text-2xl">Visitor Management System</h1>
                  <p className="text-xs text-gray-600 sm:text-sm">Monday - Friday, 9am - 5:30pm</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:justify-center sm:gap-2 sm:flex-row">
                <button
                  onClick={() => navigate('/employee-login')}
                  className="rounded-lg border border-saffron-300 px-3 py-2 text-xs font-medium text-saffron-700 transition-colors hover:bg-saffron-50 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Employee
                </button>
                <button
                  onClick={() => navigate('/admin')}
                  className="rounded-lg border border-saffron-300 px-3 py-2 text-xs font-medium text-saffron-700 transition-colors hover:bg-saffron-50 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Admin
                </button>
                <button
                  onClick={() => navigate('/security')}
                  className="rounded-lg border border-saffron-300 px-3 py-2 text-xs font-medium text-saffron-700 transition-colors hover:bg-saffron-50 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Security
                </button>
              </div>
            </div>
          </div>
        </header>

      <main className="flex-1 mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
            Visitor Registration
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Please fill in the details below. All fields marked with * are required.
          </p>
        </div>

        {errors.server && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {errors.server}
          </div>
        )}

        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-saffron-200 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="fullName" className="mb-1 block text-sm font-medium text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  placeholder="Enter your full name"
                  className={inputClass('fullName')}
                />
                {errors.fullName && <p className="mt-1 text-xs text-red-600">{errors.fullName}</p>}
              </div>

              <div>
                <label htmlFor="organization" className="mb-1 block text-sm font-medium text-gray-700">
                  Organization <span className="text-red-500">*</span>
                </label>
                <input
                  id="organization"
                  type="text"
                  value={formData.organization}
                  onChange={(e) => handleChange('organization', e.target.value)}
                  placeholder="Company/organization name"
                  className={inputClass('organization')}
                />
                {errors.organization && <p className="mt-1 text-xs text-red-600">{errors.organization}</p>}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="gender" className="mb-1 block text-sm font-medium text-gray-700">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className={inputClass('gender')}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && <p className="mt-1 text-xs text-red-600">{errors.gender}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="mb-1 block text-sm font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className={inputClass('phone')}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="you@example.com"
                className={inputClass('email')}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            <EmployeeDropdown
              value={formData.employeeId}
              onChange={(val) => handleChange('employeeId', val)}
              error={errors.employeeId}
            />

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="purpose" className="mb-1 block text-sm font-medium text-gray-700">
                  Purpose <span className="text-red-500">*</span>
                </label>
                <select
                  id="purpose"
                  value={formData.purpose}
                  onChange={(e) => handleChange('purpose', e.target.value)}
                  className={inputClass('purpose')}
                >
                  {PURPOSE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.purpose && <p className="mt-1 text-xs text-red-600">{errors.purpose}</p>}
              </div>

              <div>
                <label htmlFor="date" className="mb-1 block text-sm font-medium text-gray-700">
                  Appointment Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className={inputClass('date')}
                />
                {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
              </div>
            </div>

            {formData.purpose === 'Other' && (
              <div>
                <label htmlFor="detailedPurpose" className="mb-1 block text-sm font-medium text-gray-700">
                  Please specify your purpose <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="detailedPurpose"
                  rows={2}
                  value={formData.detailedPurpose}
                  onChange={(e) => handleChange('detailedPurpose', e.target.value)}
                  placeholder="Please describe the specific purpose of your visit"
                  className={inputClass('detailedPurpose')}
                />
                {errors.detailedPurpose && <p className="mt-1 text-xs text-red-600">{errors.detailedPurpose}</p>}
              </div>
            )}

            {formData.date && (
              <div>
                <label htmlFor="timeSlot" className="mb-1 block text-sm font-medium text-gray-700">
                  Time Slot <span className="text-red-500">*</span>
                </label>
                <select
                  id="timeSlot"
                  value={formData.timeSlot}
                  onChange={(e) => handleChange('timeSlot', e.target.value)}
                  className={inputClass('timeSlot')}
                >
                  <option value="">Select time slot</option>
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
                {errors.timeSlot && <p className="mt-1 text-xs text-red-600">{errors.timeSlot}</p>}
              </div>
            )}

            <div>
              <label htmlFor="reason" className="mb-1 block text-sm font-medium text-gray-700">
                Reason for Visit <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reason"
                rows={3}
                value={formData.reason}
                onChange={(e) => handleChange('reason', e.target.value)}
                placeholder="Please describe the reason for your visit"
                className={`${inputClass('reason')} resize-none`}
              />
              {errors.reason && <p className="mt-1 text-xs text-red-600">{errors.reason}</p>}
            </div>

            <div>
              <label htmlFor="notes" className="mb-1 block text-sm font-medium text-gray-700">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                rows={2}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional information..."
                className={`${inputClass('notes')} resize-none`}
              />
            </div>

            <SignaturePad
              value={formData.signature}
              onChange={(val) => handleChange('signature', val)}
            />
            {errors.signature && <p className="mt-1 text-xs text-red-600">{errors.signature}</p>}

            <TermsAndConditions
              accepted={formData.agreeToTerms}
              onChange={(value) => handleChange('agreeToTerms', value)}
            />
            {errors.agreeToTerms && <p className="mt-1 text-xs text-red-600">{errors.agreeToTerms}</p>}

            <button
              type="submit"
              disabled={loading || !formData.agreeToTerms}
              className="w-full rounded-lg bg-saffron-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-saffron-700 focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:ring-offset-2 active:bg-saffron-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Registering...
                </span>
              ) : (
                'Register Visit'
              )}
            </button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  )
}